import type {
  ChatSession,
  Message,
  CreateChatSessionData,
  UpdateChatSessionData,
  CreateMessageData,
  ChatQueryParams,
  MessageQueryParams,
} from 'shared/types';
import api from '../axios';

// Chat Service Class
export class ChatService {
  // Chat Session Operations
  async createSession(data: CreateChatSessionData): Promise<ChatSession> {
    const response = await api.post<ChatSession>('/chat/sessions', data);
    return response.data;
  }

  async getSessions(params?: ChatQueryParams): Promise<ChatSession[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());
    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);

    const url = `/chat/sessions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<ChatSession[]>(url);
    return response.data;
  }

  async getSessionById(sessionId: string): Promise<ChatSession> {
    const response = await api.get<ChatSession>(`/chat/sessions/${sessionId}`);
    return response.data;
  }

  async updateSession(sessionId: string, data: UpdateChatSessionData): Promise<ChatSession> {
    const response = await api.patch<ChatSession>(`/chat/sessions/${sessionId}`, data);
    return response.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await api.delete(`/chat/sessions/${sessionId}`);
  }

  // Message Operations
  async addMessage(sessionId: string, data: CreateMessageData): Promise<Message> {
    const formData = new FormData();
    formData.append('query', data.query);
    formData.append('isWebSearch', data.isWebSearch?.toString() || 'false');
    
    if (data.file) {
      formData.append('file', data.file);
    }
    
    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    const response = await api.post<Message>(`/chat/sessions/${sessionId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getMessages(sessionId: string, params?: MessageQueryParams): Promise<Message[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());

    const url = `/chat/sessions/${sessionId}/messages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<Message[]>(url);
    return response.data;
  }

  async deleteMessage(sessionId: string, messageId: string): Promise<void> {
    await api.delete(`/chat/sessions/${sessionId}/messages/${messageId}`);
  }

  // Utility Methods
  async getSessionsByTenant(tenantId: string): Promise<ChatSession[]> {
    return this.getSessions({ tenantId });
  }

  async sendUserMessage(
    sessionId: string, 
    query: string, 
    file?: File,
    isWebSearch?: boolean,
    metadata?: any
  ): Promise<Message> {
    return this.addMessage(sessionId, {
      query,
      file,
      isWebSearch,
      metadata,
    });
  }

  async sendSystemMessage(sessionId: string, query: string, metadata?: any): Promise<Message> {
    // System messages are typically used for AI responses or system notifications
    // They use the same structure as user messages but may have different metadata
    return this.addMessage(sessionId, {
      query,
      metadata: {
        ...metadata,
        messageType: 'SYSTEM',
      },
    });
  }

  async getRecentMessages(sessionId: string, limit = 50): Promise<Message[]> {
    return this.getMessages(sessionId, { take: limit, skip: 0 });
  }

  async loadMoreMessages(sessionId: string, skip: number, limit = 50): Promise<Message[]> {
    return this.getMessages(sessionId, { skip, take: limit });
  }

  // Chat Management
  async createOrGetSession(tenantId: string, title?: string): Promise<ChatSession> {
    try {
      // Try to create a new session
      return await this.createSession({ tenantId, title });
    } catch (error: any) {
      // If session already exists, get existing sessions and return the active one
      if (error.response?.status === 400) {
        const sessions = await this.getSessionsByTenant(tenantId);
        const activeSession = sessions.find(session => session.isActive);
        if (activeSession) {
          return activeSession;
        }
      }
      throw error;
    }
  }

  async searchMessages(sessionId: string, searchTerm: string): Promise<Message[]> {
    // Get all messages and filter locally for now
    // This could be optimized with backend search in the future
    const messages = await this.getMessages(sessionId);
    return messages.filter(message => 
      message.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.result?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
}

// Create and export singleton instance
export const chatService = new ChatService();

// Export individual methods for convenience
export const {
  createSession,
  getSessions,
  getSessionById,
  updateSession,
  deleteSession,
  addMessage,
  getMessages,
  deleteMessage,
  getSessionsByTenant,
  sendUserMessage,
  sendSystemMessage,
  getRecentMessages,
  loadMoreMessages,
  createOrGetSession,
  searchMessages,
} = chatService;